import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateRangeSelector } from "../../../../components/UIToolkit/molecules/DateRangeSelector";
import { SettingRow } from "../../../../components/UIToolkit/molecules/SettingRow";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import type { EmrIntegrationInput } from "../../../../utils/models";
import { useRecalculateCensus } from "./hooks";

/** Matches the hourly PCC task's window, so the default click reproduces normal behavior. */
const DEFAULT_LOOKBACK_DAYS = 14;

type Props = {
	/**
	 * The department's PCC row as it currently stands in the form. Kept whole (rather than just an
	 * `active` flag) so every reason the backend could reject the request can be explained here
	 * instead of coming back as a generic error.
	 */
	pccIntegration: EmrIntegrationInput;
	/** The form has unsaved edits — a run would use the stale facility/org ids still on the server. */
	isDirty: boolean;
};

/**
 * Support-only control to recalculate census from PCC ADT for an explicit date range
 * (Department Configuration → Integrations). The request is queued and drained by a background
 * task, so this only reports that the run was scheduled.
 */
export const CensusRecalculationSetting: FC<Props> = ({ pccIntegration, isDirty }) => {
	const { t } = useTranslation();
	const today = DateTime.now().startOf("day");
	const [startDate, setStartDate] = useState<DateTime>(today.minus({ days: DEFAULT_LOOKBACK_DAYS }));
	const [endDate, setEndDate] = useState<DateTime>(today);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const recalculateCensus = useRecalculateCensus();

	// Ordered by what the operator must fix first. Each maps to a real backend rejection:
	// an inactive or id-less PCC row is skipped by get_department_pcc_info, so the route would
	// answer "no active PCC integration" — unhelpful when the cause is a blank Facility ID.
	const disabledReason = !pccIntegration.active
		? t("department.config.integrations.emr.recalculateCensus.inactive")
		: !pccIntegration.facilityId?.trim() || !pccIntegration.orgId?.trim()
			? t("department.config.integrations.emr.recalculateCensus.missingIds")
			: isDirty
				? t("department.config.integrations.emr.recalculateCensus.dirty")
				: undefined;

	return (
		<>
			<SystemDialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				title={t("department.config.integrations.emr.recalculateCensus.confirmTitle")}
				body={t("department.config.integrations.emr.recalculateCensus.confirmBody", {
					startDate: startDate.toFormat("dd/MM/yyyy"),
					endDate: endDate.toFormat("dd/MM/yyyy"),
				})}
				confirm={t("department.config.integrations.emr.recalculateCensus.button")}
				confirmLoading={recalculateCensus.isPending}
				onConfirm={() => {
					recalculateCensus.mutate({ startDate, endDate }, { onSettled: () => setConfirmOpen(false) });
				}}
				dataTestId="recalculate-census-dialog"
			/>

			<SettingRow
				label={t("department.config.integrations.emr.recalculateCensus.label")}
				description={t("department.config.integrations.emr.recalculateCensus.description")}
				// The pickers + button are a cluster, not a single input: on the right they cannot shrink
				// and would overflow the section's contentMaxWidth, so they get their own line.
				stacked
				control={
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", sm: "row" },
							alignItems: { xs: "stretch", sm: "center" },
							flexWrap: "wrap",
							gap: 1.5,
							minWidth: 0,
						}}
					>
						<DateRangeSelector
							startDate={startDate}
							endDate={endDate}
							onStartDateChange={setStartDate}
							onEndDateChange={setEndDate}
							// Census is derived from ADT, which cannot describe the future: a future target
							// silently undercounts rather than failing, so today is a hard ceiling.
							maxDate={today}
							startDateTestId="recalculate-census-start-date"
							endDateTestId="recalculate-census-end-date"
						/>
						<SystemButton
							buttonVariant="secondary"
							onClick={() => setConfirmOpen(true)}
							loading={recalculateCensus.isPending}
							disabled={disabledReason !== undefined || recalculateCensus.isPending}
							tooltip={disabledReason}
							data-testid="recalculate-census-button"
						>
							{t("department.config.integrations.emr.recalculateCensus.button")}
						</SystemButton>
					</Box>
				}
				dataTestId="dept-config-integrations-emr-recalculate-census-row"
			/>
		</>
	);
};
